import { EditorState, AnswerKeyValue } from "../editor/types";
import { getT } from "../locales";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.yougo.com.tr/api/v1";
const ADMIN_URL =
  process.env.REACT_APP_ADMIN_URL ||
  "https://admin.yougo.com.tr/backend/questions";
const AUTH_TOKEN_STORAGE_KEY = "qe-auth-token";

const redirectToQuestionList = () => {
  if (typeof window === "undefined") {
    return;
  }
  window.location.replace(ADMIN_URL);
};

const getTokenFromUrl = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");
  return token ? token.trim() : null;
};

const getTokenFromStorage = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const token = window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    return token ? token.trim() : null;
  } catch (_error) {
    return null;
  }
};

const persistToken = (token: string | null) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (token) {
      window.localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
    } else {
      window.localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    }
  } catch (_error) {
    // Silently ignore storage issues (privacy mode etc.)
  }
};

export interface QuestionData {
  type: "draft" | "template" | "question";
  title?: string; // Template name (only for templates)
  tags?: string[]; // Template tags (only for templates)
  questionType?: { id: string | number; name: string };
  questionTypeId?: number;
  questionCode?: string;
  difficulty?: number;
  lessonId?: string | number;
  gradeId?: string | number;
  moduleId?: string | number;
  outcomeIds?: string[];
  answerKey?: Record<string, AnswerKeyValue>;
  trackTime?: boolean;
  timeLimitSeconds?: number;
  solutionVideoUrl?: string;
  topicVideoUrl?: string;
  solutionTextUrl?: string;
  notMobileFriendly?: boolean;
  displayInMba?: boolean;
  canvas: Record<string, any>;
  elements: Record<string, any>;
  assets?: Record<string, any>;
  screenshotFile?: {
    name: string;
    size: number;
    type: string;
    lastModified: number;
    base64: string; // File'ın base64 versiyonu
  };
}

export interface SaveQuestionResponse {
  success: boolean;
  questionId?: string;
  message?: string;
  errors?: string[];
}

export interface GetQuestionResponse {
  status: string;
  message: string;
  data: {
    id: number;
    type: string;
    question: any;
  };
}

export interface Lesson {
  id: string;
  name: string;
}

export interface Grade {
  id: string;
  name: string;
  description: string;
}

export interface Outcome {
  id: string;
  code: string;
  description: string;
  subject_id?: string;
}

export interface Subject {
  id: string;
  name: string;
  displayName: string;
  description: string;
  parent_subject_id: string | null;
  grade_id: string;
  lesson_id: string;
  outcomes: Outcome[];
  children: Subject[];
  has_children: boolean;
}

export interface QuestionType {
  id: string;
  name: string;
  displayName: string | null;
}

export interface Template {
  id: string | number;
  title: string;
  description?: string;
  thumbnail?: string;
  tags?: string[];
  questionType?: QuestionType;
  screenshotFile?: {
    media_id: number;
    url: string;
  };
}

export interface DefinitionsData {
  lessons: Lesson[];
  grades: Grade[];
  subjects: Subject[];
  relationships: Array<{
    lesson_id: string;
    grade_id: string;
    subject_id: string;
  }>;
  outcomes: Outcome[];
  questionTypes: QuestionType[];
  templates?: Template[];
  tags?: string[];
}

export interface DefinitionsResponse {
  status: string;
  message: string;
  data: DefinitionsData;
}

export interface MediaItem {
  id: string | number | null;
  url: string;
  name?: string;
  file_name?: string;
  type?: string;
  mime_type?: string;
  size?: number;
  size_formatted?: string;
  thumbnail?: string;
  created_at?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface TextToSpeechPayload {
  text: string;
  voice_id: string;
  name?: string;
  model_id?: string;
  language_code?: string;
  output_format?: string;
  voice_settings?: Record<string, unknown>;
}

export interface TextToSpeechMedia {
  media_id: string | number;
  name: string;
  type: string;
  mime_type: string;
  size: number;
  url: string;
  path?: string;
  file_name?: string;
  metadata?: Record<string, unknown> | null;
}

export interface SceneMedia {
  type: "image" | "audio" | "video" | string;
  mime_type: string;
  size: number;
  url: string;
  path: string;
  file_name: string;
  folder: string;
  user_folder: string;
}

export interface SceneMediaResponse {
  data: SceneMedia;
  message: string;
  code: number;
}

export interface DeleteTextToSpeechPayload {
  path: string;
}

export interface MediaLibraryCategory {
  images?: MediaItem[];
}

export interface MediaLibraryData {
  [category: string]: MediaLibraryCategory | MediaItem[];
}

export interface MediaLibraryResponse {
  status?: string;
  message?: string;
  code?: number;
  data?: MediaLibraryData & {
    rootImages?: MediaItem[];
  };
  items?: MediaItem[];
}

export interface MediaGroup {
  category: string;
  items: MediaItem[];
}

export interface EducationalMaterialData {
  grade: {
    id: number;
    name: string;
  };
  lessonId: string;
  lessonName: string;
  title: string;
  video: Array<string | { title: string; url: string }>;
  others: Array<string | { title: string; url: string }>;
}

export interface EducationalMaterialResponse {
  data: EducationalMaterialData;
  message: string;
  code: number;
}

class ApiService {
  private baseUrl: string;
  private authToken: string | null = null;
  private mediaLibraryCache: MediaItem[] | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
    const urlToken = getTokenFromUrl();
    if (!urlToken) {
      const storedToken = getTokenFromStorage();
      if (storedToken) {
        this.authToken = storedToken;
      } else {
        this.authToken = null;
        redirectToQuestionList();
        return;
      }
    }

    if (urlToken) {
      this.authToken = urlToken;
      persistToken(urlToken);
    }
  }

  setAuthToken(token: string | null) {
    this.authToken = token ? token.trim() : null;
    persistToken(this.authToken);
  }

  private resolveAuthToken(): string | null {
    if (typeof window !== "undefined") {
      const urlToken = getTokenFromUrl();
      if (!urlToken) {
        const storedToken = getTokenFromStorage();
        if (storedToken) {
          this.authToken = storedToken;
          return storedToken;
        }
        this.authToken = null;
        redirectToQuestionList();
        return null;
      }

      if (urlToken !== this.authToken) {
        this.authToken = urlToken;
        persistToken(urlToken);
      }

      return urlToken;
    }

    return this.authToken;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers = new Headers(options.headers as HeadersInit | undefined);
    const isFormDataBody =
      typeof FormData !== "undefined" && options.body instanceof FormData;
    if (!isFormDataBody && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    const token = this.resolveAuthToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        let errorData: any = {};
        try {
          errorData = await response.json();
        } catch {
          // If JSON parsing fails, use status text
          errorData = { message: response.statusText };
        }

        // Create error with more context
        const errorMessage =
          errorData.message ||
          errorData.error ||
          `HTTP ${response.status}: ${response.statusText}`;
        const error = new Error(errorMessage);

        // Attach additional error info for better parsing
        (error as any).status = response.status;
        (error as any).errors =
          errorData.errors || errorData.validation_errors || [];
        (error as any).errorData = errorData;

        throw error;
      }

      return await response.json();
    } catch (error) {
      // Re-throw with better context if it's not already an Error
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(String(error));
    }
  }

  async saveQuestion(
    questionData: QuestionData
  ): Promise<SaveQuestionResponse> {
    try {
      const response = await this.request<any>("/questions", {
        method: "POST",
        body: JSON.stringify(questionData),
      });

      // If response has success field, use it; otherwise treat 2xx as success
      return {
        success: response.success !== undefined ? response.success : true,
        questionId: response.questionId || response.id || response.data?.id,
        message: response.message,
        errors: response.errors,
      };
    } catch (error) {
      const t = getT();
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      return {
        success: false,
        message: errorObj.message || t("api.error.unknown"),
        errors: (errorObj as any).errors || [],
      };
    }
  }

  async updateQuestion(
    questionId: string,
    questionData: QuestionData
  ): Promise<SaveQuestionResponse> {
    try {
      const response = await this.request<any>(`/questions/${questionId}`, {
        method: "PUT",
        body: JSON.stringify(questionData),
      });

      // If response has success field, use it; otherwise treat 2xx as success
      return {
        success: response.success !== undefined ? response.success : true,
        questionId: response.questionId || response.id || response.data?.id,
        message: response.message,
        errors: response.errors,
      };
    } catch (error) {
      const t = getT();
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      return {
        success: false,
        message: errorObj.message || t("api.error.unknown"),
        errors: (errorObj as any).errors || [],
      };
    }
  }

  async getQuestion(questionId: string): Promise<GetQuestionResponse> {
    return this.request<GetQuestionResponse>(`/questions/${questionId}`);
  }

  async getTemplate(templateId: string | number): Promise<GetQuestionResponse> {
    return this.request<GetQuestionResponse>(`/questions/${templateId}`);
  }

  async getDefinitions(): Promise<DefinitionsResponse> {
    try {
      const response = await this.request<DefinitionsResponse>("/definitions");
      return response;
    } catch (error) {
      if ((error as { status?: number } | null)?.status === 401) {
        throw error;
      }
      return {
        status: "error",
        message: "Failed to fetch definitions",
        data: {
          lessons: [],
          grades: [],
          subjects: [],
          relationships: [],
          outcomes: [],
          questionTypes: [],
          templates: [],
          tags: [],
        },
      };
    }
  }

  async getMediaLibrary(): Promise<MediaItem[]> {
    // Return cached data if available
    if (this.mediaLibraryCache !== null) {
      return this.mediaLibraryCache;
    }

    try {
      const response = await this.request<MediaLibraryResponse>(
        "/media-library/all-media"
      );

      let allImages: MediaItem[] = [];

      // Handle different response structures
      if (Array.isArray(response)) {
        allImages = response.filter((item) => item.type === "image");
      } else if (response.data) {
        // Collect images from all categories
        Object.keys(response.data).forEach((category) => {
          // Skip rootImages as it's handled separately
          if (category === "rootImages") {
            return;
          }

          const categoryData = response.data![category];
          if (categoryData) {
            // Check if it's a MediaLibraryCategory with images array
            if (
              typeof categoryData === "object" &&
              "images" in categoryData &&
              Array.isArray(categoryData.images)
            ) {
              allImages.push(...categoryData.images);
            }
            // Check if it's directly an array of MediaItems
            else if (Array.isArray(categoryData)) {
              allImages.push(...categoryData);
            }
          }
        });

        // Add root images if they exist
        if (
          "rootImages" in response.data &&
          Array.isArray(response.data.rootImages)
        ) {
          allImages.push(...response.data.rootImages);
        }

        // Filter only images (exclude videos, etc.)
        allImages = allImages.filter((item) => item.type === "image");
      } else if (response.items && Array.isArray(response.items)) {
        allImages = response.items.filter((item) => item.type === "image");
      }

      // Cache the result
      this.mediaLibraryCache = allImages;
      return allImages;
    } catch (error) {
      console.error("Failed to fetch media library:", error);
      return [];
    }
  }

  async getGroupedMediaLibrary(): Promise<MediaGroup[]> {
    try {
      const t = getT();
      const response = await this.request<MediaLibraryResponse>(
        "/media-library/all-media"
      );

      const groups: MediaGroup[] = [];

      // Handle different response structures
      if (Array.isArray(response)) {
        const images = response.filter((item) => item.type === "image");
        if (images.length > 0) {
          groups.push({ category: t("api.media.allImages"), items: images });
        }
      } else if (response.data) {
        // Collect images by category
        Object.keys(response.data).forEach((category) => {
          // Handle rootImages separately
          if (category === "rootImages") {
            return;
          }

          const categoryData = response.data![category];
          let categoryImages: MediaItem[] = [];

          if (categoryData) {
            // Check if it's a MediaLibraryCategory with images array
            if (
              typeof categoryData === "object" &&
              "images" in categoryData &&
              Array.isArray(categoryData.images)
            ) {
              categoryImages = categoryData.images.filter(
                (item) => item.type === "image"
              );
            }
            // Check if it's directly an array of MediaItems
            else if (Array.isArray(categoryData)) {
              categoryImages = categoryData.filter(
                (item) => item.type === "image"
              );
            }
          }

          if (categoryImages.length > 0) {
            groups.push({ category, items: categoryImages });
          }
        });

        // Add root images as first group if they exist
        if (
          "rootImages" in response.data &&
          Array.isArray(response.data.rootImages)
        ) {
          const rootImages = response.data.rootImages.filter(
            (item) => item.type === "image"
          );
          if (rootImages.length > 0) {
            groups.unshift({ category: t("api.media.other"), items: rootImages });
          }
        }
      } else if (response.items && Array.isArray(response.items)) {
        const images = response.items.filter((item) => item.type === "image");
        if (images.length > 0) {
          groups.push({ category: t("api.media.allImages"), items: images });
        }
      }

      return groups;
    } catch (error) {
      console.error("Failed to fetch grouped media library:", error);
      return [];
    }
  }

  async getGroupedSoundLibrary(): Promise<MediaGroup[]> {
    try {
      const t = getT();
      const response = await this.request<MediaLibraryResponse>(
        "/media-library/all-audio"
      );

      const groups: MediaGroup[] = [];

      // Handle different response structures
      if (Array.isArray(response)) {
        const audios = response.filter((item) => item.type === "audio");
        if (audios.length > 0) {
          groups.push({ category: t("api.audio.allSounds"), items: audios });
        }
      } else if (response.data) {
        // Collect audios by category
        Object.keys(response.data).forEach((category) => {
          // Handle rootImages separately (note: API returns rootImages even for audio)
          if (category === "rootImages") {
            return;
          }

          const categoryData = response.data![category];
          let categoryAudios: MediaItem[] = [];

          if (categoryData) {
            // Check if it's a MediaLibraryCategory with images array (API uses "images" key even for audio)
            if (
              typeof categoryData === "object" &&
              "images" in categoryData &&
              Array.isArray(categoryData.images)
            ) {
              categoryAudios = categoryData.images.filter(
                (item) => item.type === "audio"
              );
            }
            // Check if it's directly an array of MediaItems
            else if (Array.isArray(categoryData)) {
              categoryAudios = categoryData.filter(
                (item) => item.type === "audio"
              );
            }
          }

          if (categoryAudios.length > 0) {
            groups.push({ category, items: categoryAudios });
          }
        });

        // Add root audios as first group if they exist (API returns as rootImages)
        if (
          "rootImages" in response.data &&
          Array.isArray(response.data.rootImages)
        ) {
          const rootAudios = response.data.rootImages.filter(
            (item) => item.type === "audio"
          );
          if (rootAudios.length > 0) {
            groups.unshift({ category: t("api.media.other"), items: rootAudios });
          }
        }
      } else if (response.items && Array.isArray(response.items)) {
        const audios = response.items.filter((item) => item.type === "audio");
        if (audios.length > 0) {
          groups.push({ category: t("api.audio.allSounds"), items: audios });
        }
      }

      return groups;
    } catch (error) {
      console.error("Failed to fetch grouped sound library:", error);
      return [];
    }
  }

  // Clear media library cache (useful for manual refresh if needed)
  clearMediaLibraryCache(): void {
    this.mediaLibraryCache = null;
  }

  async generateSpeech(payload: TextToSpeechPayload): Promise<TextToSpeechMedia> {
    const response = await this.request<
      TextToSpeechMedia | { data?: TextToSpeechMedia }
    >("/text-to-speech", {
      method: "POST",
      body: JSON.stringify(payload),
    });

    const media = "data" in response && response.data ? response.data : response;
    this.clearMediaLibraryCache();
    return media as TextToSpeechMedia;
  }

  async saveSceneMedia(file: File): Promise<SceneMedia> {
    const payload = new FormData();
    payload.append("file", file);

    const response = await this.request<SceneMediaResponse>("/media/scene", {
      method: "POST",
      body: payload,
    });

    if (!response.data?.url) {
      throw new Error(response.message || getT()("localMedia.missingUrlError"));
    }

    this.clearMediaLibraryCache();
    return response.data;
  }

  async deleteGeneratedSpeech(
    payload: DeleteTextToSpeechPayload
  ): Promise<void> {
    await this.request<unknown>("/text-to-speech", {
      method: "DELETE",
      body: JSON.stringify(payload),
    });

    this.clearMediaLibraryCache();
  }

  async getEducationalMaterials(
    subjectId: string
  ): Promise<EducationalMaterialResponse> {
    try {
      const response = await this.request<EducationalMaterialResponse>(
        `/educational-materials/${subjectId}`
      );
      return response;
    } catch (error) {
      console.error("Failed to fetch educational materials:", error);
      throw error;
    }
  }

  // EditorState'den QuestionData'ya dönüşüm
  static formatEditorStateToQuestionData(
    editorState: EditorState,
    type: "draft" | "template" | "question" = "question",
    screenshotFile?: File | null, // File object (optional, will be converted to metadata)
    screenshotBase64?: string | null, // Base64 encoded canvas screenshot (for screenshotFile.base64)
    title?: string, // Template title (only for templates)
    isUpdate?: boolean, // If true, this is an update operation
    currentType?: "draft" | "template" | "question" | null, // Current type of the question being updated
    tags?: string[] // Template tags (only for templates)
  ): QuestionData {
    const parseOptionalNumber = (
      value?: string | number | null
    ): number | undefined => {
      if (value === undefined || value === null || value === "") {
        return undefined;
      }
      const numeric = typeof value === "number" ? value : Number(value);
      return Number.isFinite(numeric) ? numeric : undefined;
    };

    const normalizeTags = (values: string[]): string[] => {
      const seen = new Map<string, string>();
      values.forEach((tag) => {
        const trimmed = tag.trim();
        if (!trimmed) {
          return;
        }
        const key = trimmed.toLocaleLowerCase("tr-TR");
        if (!seen.has(key)) {
          seen.set(key, trimmed);
        }
      });
      return Array.from(seen.values());
    };

    // Preserve question code only when:
    // 1. It's an update operation (isUpdate === true)
    // 2. The type hasn't changed (currentType === type)
    // For new records or type changes, always generate a new question code
    const shouldPreserveQuestionCode =
      isUpdate &&
      currentType !== null &&
      currentType !== undefined &&
      currentType === type &&
      editorState.canvas.question?.questionCode;

    const questionCode = shouldPreserveQuestionCode
      ? editorState.canvas.question.questionCode
      : `Q-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Convert learningOutcomes string to array
    const outcomeIds = editorState.canvas.question?.learningOutcomes
      ? [editorState.canvas.question.learningOutcomes]
      : undefined;

    const trackTime = editorState.canvas.question?.trackTime ?? false;
    const rawTimeLimit = editorState.canvas.question?.timeLimitSeconds ?? 0;
    const sanitizedTimeLimit = trackTime
      ? Math.max(0, rawTimeLimit)
      : undefined;

    const questionTypeId = parseOptionalNumber(
      editorState.canvas.question?.questionType?.id
    );
    const questionTypeName =
      editorState.canvas.question?.questionType?.name?.trim();
    const lessonId = parseOptionalNumber(editorState.canvas.question?.lesson);
    const gradeId = parseOptionalNumber(editorState.canvas.question?.grade);
    const subjectId = parseOptionalNumber(editorState.canvas.question?.subject);

    const questionType =
      questionTypeId !== undefined && questionTypeName
        ? { id: String(questionTypeId), name: questionTypeName }
        : undefined;

    const rawTags =
      Array.isArray(tags) && tags
        ? tags
        : Array.isArray(editorState.canvas.question?.tags)
        ? editorState.canvas.question?.tags
        : undefined;
    const normalizedTags = rawTags ? normalizeTags(rawTags) : undefined;

    // Başlangıç answerKey'i al (defensive: backend/older payloads may contain null)
    const rawAnswerKey = (editorState.canvas.question as any)?.answerKey;
    const baseAnswerKey: Record<string, AnswerKeyValue> =
      rawAnswerKey &&
      typeof rawAnswerKey === "object" &&
      !Array.isArray(rawAnswerKey)
        ? rawAnswerKey
        : {};
    const answerKey: Record<string, AnswerKeyValue> = { ...baseAnswerKey };

    // Guard against empty/whitespace keys which can break backend persistence
    Object.keys(answerKey).forEach((key) => {
      if (!key || key.trim() === "") {
        delete (answerKey as any)[key];
      }
    });

    // Matching point'ler için connections'ları answerKey'e aktar
    const sourcePoints = editorState.elements.filter(
      (element) =>
        element.type === "matching-point" &&
        (element.data as { pointType?: "source" | "target" })?.pointType ===
          "source"
    );

    if (sourcePoints.length > 0) {
      const connections: Array<{
        sourcePointId: string;
        targetPointId: string;
      }> = [];

      sourcePoints.forEach((sourcePoint) => {
        const sourceData = sourcePoint.data as { connections?: string[] };
        const connectionsList = sourceData.connections || [];
        connectionsList.forEach((targetId) => {
          // Verify target point exists
          const targetPoint = editorState.elements.find(
            (el) =>
              el.id === targetId &&
              el.type === "matching-point" &&
              (el.data as { pointType?: "source" | "target" })?.pointType ===
                "target"
          );
          if (targetPoint) {
            connections.push({
              sourcePointId: sourcePoint.id,
              targetPointId: targetId,
            });
          }
        });
      });

      // Update or create matching-connections in answerKey
      if (connections.length > 0) {
        answerKey["matching-connections"] = { connections };
      } else {
        // If no connections found, remove matching-connections from answerKey
        // This allows users to clear all matching connections
        delete answerKey["matching-connections"];
      }
    }

    // Boşluk doldurma elemanları için cevap anahtarını otomatik olarak ekle
    editorState.elements.forEach((element) => {
      if (element.type === "fill-in-the-blanks") {
        const data = element.data as {
          content?: string;
          blanks?: Array<{ id: string; value: string }>;
        };
        const content = data.content || "";
        const blanks = data.blanks || [];

        // blanks array'inden ID ve değerleri al, yoksa content'ten parse et
        let blankAnswers: Array<{ id: string; value: string }> = [];
        if (blanks.length > 0) {
          // blanks array'inden direkt ID ve value'ları al
          blankAnswers = blanks.map((blank) => ({
            id: blank.id,
            value: blank.value,
          }));
        } else {
          // Fallback: content'ten regex ile parse et (ID oluştur)
          const matches = content.match(/\[(.*?)\]/g);
          if (matches) {
            blankAnswers = matches.map((match, index) => ({
              id: `blank-${Date.now()}-${index}`,
              value: match.slice(1, -1), // Remove brackets
            }));
          }
        }

        // Cevap anahtarına ekle (boş değilse ve zaten yoksa)
        if (blankAnswers.length > 0 && !answerKey[element.id]) {
          answerKey[element.id] = {
            blanks: blankAnswers,
          };
        }
      }
    });

    // Painting answer key'lerini temizle: boş paintSwatchId/paintColor kayıtlarını kaldır
    Object.keys(answerKey).forEach((key) => {
      const value = answerKey[key];
      if (
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        ("paintSwatchId" in value || "paintColor" in value)
      ) {
        const paintingConfig = value as {
          paintSwatchId?: string;
          paintColor?: string;
        };
        const hasSwatch =
          !!paintingConfig.paintSwatchId &&
          paintingConfig.paintSwatchId.trim() !== "";
        const hasColor =
          !!paintingConfig.paintColor &&
          paintingConfig.paintColor.trim() !== "";
        if (!hasSwatch && !hasColor) {
          delete answerKey[key];
        }
      }
    });
    const hasAnswerKey = Object.keys(answerKey).length > 0;

    const questionData: QuestionData = {
      type,
      title: type === "template" && title ? title.trim() : undefined,
      tags: type === "template" && rawTags ? normalizedTags : undefined,
      questionType,
      questionTypeId,
      questionCode,
      difficulty: editorState.canvas.question?.difficulty || 1,
      lessonId: lessonId !== undefined ? String(lessonId) : undefined,
      gradeId: gradeId !== undefined ? String(gradeId) : undefined,
      moduleId: subjectId !== undefined ? String(subjectId) : undefined,
      outcomeIds,
      // Important: do NOT send answerKey: null. If empty, omit the field.
      answerKey: hasAnswerKey ? answerKey : undefined,
      trackTime,
      timeLimitSeconds: sanitizedTimeLimit,
      solutionVideoUrl:
        editorState.canvas.question?.solutionVideoUrl?.trim() || undefined,
      topicVideoUrl:
        editorState.canvas.question?.topicVideoUrl?.trim() || undefined,
      solutionTextUrl:
        editorState.canvas.question?.solutionTextUrl?.trim() || undefined,
      notMobileFriendly:
        editorState.canvas.question?.notMobileFriendly ?? false,
      displayInMba: editorState.canvas.question?.displayInMba ?? false,
      canvas: {
        width: editorState.canvas.width,
        height: editorState.canvas.height,
        background: editorState.canvas.background,
        expandable: editorState.canvas.expandable,
        lockWidth: editorState.canvas.lockWidth,
        padding: editorState.canvas.padding,
        innerPadding: editorState.canvas.innerPadding,
        grid: editorState.canvas.grid,
        hiddenArea: editorState.canvas.hiddenArea,
      },
      elements: editorState.elements.reduce((acc, element) => {
        let elementData = element.data;

        // Convert boolean values to 0/1 for order-list showLabel (backend expects 0/1)
        if (element.type === "order-list" && "showLabel" in elementData) {
          const showLabel = elementData.showLabel;
          const showLabelValue =
            showLabel === false ||
            showLabel === 0 ||
            showLabel === "0" ||
            showLabel === "" ||
            showLabel == null
              ? 0
              : 1;
          elementData = {
            ...elementData,
            showLabel: showLabelValue,
          };
        }

        // Filter out default appearance values for fill-in-the-blanks
        // Only send values that differ from defaults
        if (element.type === "fill-in-the-blanks" && elementData.appearance) {
          const appearance = elementData.appearance as any;
          const defaultAppearance = {
            italic: false,
            underline: false,
            inputBorderStyle: "full",
            shadow: "none",
            textTransform: "none",
          };

          const filteredAppearance: any = {};
          Object.keys(appearance).forEach((key) => {
            const value = appearance[key];
            const defaultValue = (defaultAppearance as any)[key];

            // Only include if value differs from default or is not a default property
            if (defaultValue !== undefined) {
              if (value !== defaultValue) {
                filteredAppearance[key] = value;
              }
            } else {
              // Include non-default properties
              filteredAppearance[key] = value;
            }
          });

          // Only include appearance if it has non-default values
          if (Object.keys(filteredAppearance).length > 0) {
            elementData = {
              ...elementData,
              appearance: filteredAppearance,
            };
          } else {
            // Remove appearance entirely if all values are defaults
            const { appearance: _, ...rest } = elementData;
            elementData = rest;
          }
        }

        // Filter out editor-only fields from push-to-talk appearance and normalize values
        if (element.type === "push-to-talk" && elementData.appearance) {
          const appearance = elementData.appearance as any;
          const filteredAppearance: any = { ...appearance };

          // Remove editor-only fields that shouldn't be saved/exported
          delete filteredAppearance.editorPreviewStatus;

          // Normalize boolean values to "0"/"1" strings (backend expects this format)
          const booleanFields = [
            "showTranscription",
            "showMicButton",
            "showErrorAlert",
            "showManualInput",
            "showManualInputLabel",
          ];
          booleanFields.forEach((field) => {
            if (field in filteredAppearance) {
              const value = filteredAppearance[field];
              filteredAppearance[field] =
                value === false ||
                value === 0 ||
                value === "0" ||
                value === "" ||
                value == null
                  ? "0"
                  : "1";
            }
          });

          // Normalize numeric string values to actual numbers
          const numericFields = [
            "maxDurationSeconds",
            "borderWidth",
            "borderRadius",
            "buttonSizePx",
            "buttonContentSizePx",
            "buttonTextFontSize",
            "buttonCornerRadiusPx",
            "buttonBorderWidthPx",
            "buttonPaddingPx",
          ];
          numericFields.forEach((field) => {
            if (field in filteredAppearance) {
              const value = filteredAppearance[field];
              if (typeof value === "string") {
                const numValue = Number(value);
                if (!isNaN(numValue)) {
                  filteredAppearance[field] = numValue;
                }
              }
            }
          });

          // Only include appearance if it has values
          if (Object.keys(filteredAppearance).length > 0) {
            elementData = {
              ...elementData,
              appearance: filteredAppearance,
            };
          }
        }

        acc[element.id] = {
          id: element.id,
          type: element.type,
          position: element.position,
          size: element.size,
          rotation: element.rotation,
          zIndex: element.zIndex,
          appearOrder: element.appearOrder,
          appearDelayMs: element.appearDelayMs,
          minDisplayMs: element.minDisplayMs,
          data: elementData,
        };
        return acc;
      }, {} as Record<string, any>),
      assets: editorState.assets.reduce((acc, asset) => {
        acc[asset.id] = {
          name: asset.name,
          type: asset.type,
          meta: asset.meta,
        };
        return acc;
      }, {} as Record<string, any>),
      screenshotFile:
        screenshotFile && screenshotBase64
          ? {
              name: screenshotFile.name,
              size: screenshotFile.size,
              type: screenshotFile.type,
              lastModified: screenshotFile.lastModified,
              base64: screenshotBase64, // Base64 string
            }
          : undefined,
    };
    return questionData;
  }
}

export { ApiService };
export const apiService = new ApiService();
export default apiService;
